<script setup lang="ts">
// Modal — единая модалка студии и платформы (без внешних зависимостей):
// Teleport в body, dim-фон, закрытие по Esc/клику вне, фокус при открытии,
// блокировка скролла страницы. Слоты: header, default (тело), footer (кнопки).
import { onBeforeUnmount, ref, watch } from 'vue';

const props = withDefaults(defineProps<{
  open: boolean;
  title?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** Клик по dim-фону закрывает (Esc закрывает всегда). */
  closeOnOutside?: boolean;
}>(), { size: 'md', closeOnOutside: true, title: '', description: '' });

const emit = defineEmits<{ 'update:open': [boolean] }>();

const widths: Record<string, string> = { sm: '28rem', md: '36rem', lg: '48rem', xl: '64rem' };
const titleId = `ui-modal-title-${Math.random().toString(36).slice(2, 8)}`;

function close(): void {
  emit('update:open', false);
}

function onKeydown(e: KeyboardEvent): void {
  if (e.key === 'Escape' && props.open) close();
}

watch(() => props.open, (v) => {
  if (v) {
    document.addEventListener('keydown', onKeydown);
    document.body.style.overflow = 'hidden';
  } else {
    document.removeEventListener('keydown', onKeydown);
    document.body.style.overflow = '';
  }
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onKeydown);
  document.body.style.overflow = '';
});
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="ui-modal-overlay" @click="closeOnOutside ? close() : undefined" />
    <div
      v-if="open"
      class="ui-modal"
      :style="{ maxWidth: widths[size] }"
      role="dialog"
      aria-modal="true"
      :aria-labelledby="title ? titleId : undefined"
      @click.stop
    >
      <header v-if="title || description || $slots.header" class="ui-modal-header">
        <div class="ui-modal-heading">
          <h2 v-if="title" :id="titleId" class="ui-modal-title">{{ title }}</h2>
          <p v-if="description" class="ui-modal-desc">{{ description }}</p>
        </div>
        <slot name="header" />
        <button class="ui-modal-close" aria-label="Закрыть" @click="close">✕</button>
      </header>
      <div class="ui-modal-body"><slot /></div>
      <footer v-if="$slots.footer" class="ui-modal-footer"><slot name="footer" /></footer>
    </div>
  </Teleport>
</template>
